/**
 * Kaleos × FittingBox Virtual Try-On Advanced
 *
 * - Shows "Try on" buttons ([data-vto-sku]) only for SKUs FittingBox has a 3D model for
 *   (Product API "availability" endpoint, batched, max 150 ids per call).
 * - Loads the FittingBox library and creates ONE try-on instance per page, and only if
 *   at least one frame on the page is available.
 * - On click: full-screen modal asks Woman / Man (setUserInfo → average PD so the frame
 *   is shown at the right size), then setFrame → startVto('live'). Camera only, no carousel.
 * - Watches the DOM for new buttons (infinite scroll, product page colour switch, quick view).
 *
 * Config comes from window.KaleosVTO (rendered in layout/theme.liquid).
 * Docs: https://fittingbox.com/en/resources/help-center/virtual-try-on-advanced
 */
(() => {
  'use strict';

  const cfg = window.KaleosVTO;
  if (!cfg || !cfg.apiKey || window.__kaleosVtoLoaded) return;
  window.__kaleosVtoLoaded = true;

  const LIBRARY_URL = 'https://vto-advanced-integration-api.fittingbox.com/index.js';
  const AVAILABILITY_URL = 'https://product-api.fittingbox.com/glasses-metadata/availability';
  const BATCH_SIZE = 150; // FittingBox limit per availability call
  const BUTTON_SELECTOR = '[data-vto-sku]';
  const CONTAINER_ID = 'kaleos-vto-container';

  const log = (...args) => cfg.debug && console.log('[Kaleos VTO]', ...args);

  /* ------------------------------------------------------------------ */
  /* Availability                                                        */
  /* ------------------------------------------------------------------ */

  const availability = new Map(); // sku -> boolean
  const inFlight = new Map(); // sku -> Promise

  async function fetchAvailability(skus) {
    const url = `${AVAILABILITY_URL}?apiKey=${encodeURIComponent(cfg.apiKey)}&uidList=${skus
      .map(encodeURIComponent)
      .join(',')}`;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const list = await res.json();
      const found = new Set();
      list.forEach((item) => {
        availability.set(item.uid, Boolean(item.available));
        found.add(item.uid);
      });
      skus.forEach((sku) => found.has(sku) || availability.set(sku, false));
    } catch (err) {
      // Network/API problem: keep buttons hidden rather than show a broken try-on.
      console.warn('[Kaleos VTO] availability check failed', err);
      skus.forEach((sku) => availability.set(sku, false));
    }
  }

  /** Resolves when every given SKU has a known availability. */
  function ensureAvailability(skus) {
    const unknown = [...new Set(skus)].filter((s) => s && !availability.has(s) && !inFlight.has(s));
    for (let i = 0; i < unknown.length; i += BATCH_SIZE) {
      const batch = unknown.slice(i, i + BATCH_SIZE);
      const p = fetchAvailability(batch).finally(() => batch.forEach((s) => inFlight.delete(s)));
      batch.forEach((s) => inFlight.set(s, p));
    }
    const waits = [...new Set(skus.map((s) => inFlight.get(s)).filter(Boolean))];
    return Promise.all(waits);
  }

  /* ------------------------------------------------------------------ */
  /* Buttons                                                             */
  /* ------------------------------------------------------------------ */

  let scanQueued = false;

  function queueScan() {
    if (scanQueued) return;
    scanQueued = true;
    // Small delay so a page full of cards (or an infinite-scroll page) becomes one API call.
    setTimeout(scan, 50);
  }

  async function scan() {
    scanQueued = false;
    const buttons = [...document.querySelectorAll(`${BUTTON_SELECTOR}:not([data-vto-state])`)];
    if (!buttons.length) return;
    buttons.forEach((b) => (b.dataset.vtoState = 'checking'));

    await ensureAvailability(buttons.map((b) => b.dataset.vtoSku));

    let anyAvailable = false;
    buttons.forEach((b) => {
      if (availability.get(b.dataset.vtoSku)) {
        b.dataset.vtoState = 'ready';
        b.hidden = false;
        anyAvailable = true;
      } else {
        b.dataset.vtoState = 'unavailable';
      }
    });

    // Create the try-on early (FittingBox best practice) but only when it will be used.
    if (anyAvailable) ensureWidget().catch(() => {});
  }

  /* ------------------------------------------------------------------ */
  /* FittingBox instance (created once per page)                         */
  /* ------------------------------------------------------------------ */

  let widget = null;
  let widgetPromise = null;
  let isOpen = false;
  let isStarting = false;

  function loadLibrary() {
    if (window.FitMix) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = LIBRARY_URL;
      s.onload = resolve;
      s.onerror = () => reject(new Error('FittingBox library failed to load'));
      document.head.appendChild(s);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Full-screen modal: gender step → embedded camera try-on             */
  /* ------------------------------------------------------------------ */

  const GENDER_KEY = 'kaleosVtoGender';
  const escapeHtml = (str) =>
    String(str || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  let modal = null;
  let lastTrigger = null;
  let pendingSku = null;
  let closeFallback = null;

  function buildModal() {
    if (modal) return modal;
    const t = cfg.strings || {};
    modal = document.createElement('div');
    modal.className = 'vto-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', t.tryOn || 'Try on');
    modal.setAttribute('aria-hidden', 'true');
    modal.innerHTML = `
      <div class="vto-modal__vto" id="${CONTAINER_ID}"></div>
      <div class="vto-gender" role="group" aria-labelledby="vto-gender-title">
        <div class="vto-gender__inner">
          <p class="vto-gender__title" id="vto-gender-title">${escapeHtml(t.genderTitle || 'Who are they for?')}</p>
          <p class="vto-gender__text">${escapeHtml(t.genderText || '')}</p>
          <div class="vto-gender__options">
            <button type="button" class="vto-gender__option" data-vto-gender="f">${escapeHtml(t.woman || 'Woman')}</button>
            <button type="button" class="vto-gender__option" data-vto-gender="m">${escapeHtml(t.man || 'Man')}</button>
          </div>
        </div>
      </div>
      <button type="button" class="vto-modal__close" aria-label="${escapeHtml(t.close || 'Close')}">
        <svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 3l10 10M13 3L3 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>`;
    modal.querySelector('.vto-modal__close').addEventListener('click', closeTryOn);
    modal.querySelectorAll('[data-vto-gender]').forEach((btn) =>
      btn.addEventListener('click', () => startCamera(btn.dataset.vtoGender))
    );
    document.body.appendChild(modal);
    return modal;
  }

  function showGenderStep() {
    buildModal();
    modal.classList.add('is-open', 'is-gender-step');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('vto-open');

    // Pre-focus the last choice in this browsing session (still one click to confirm).
    let last = null;
    try {
      last = sessionStorage.getItem(GENDER_KEY);
    } catch (e) {}
    const target =
      modal.querySelector(`[data-vto-gender="${last}"]`) || modal.querySelector('[data-vto-gender]');
    target.focus({ preventScroll: true });
  }

  function hideModal() {
    clearTimeout(closeFallback);
    if (!modal) return;
    modal.classList.remove('is-open', 'is-gender-step');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('vto-open');
    pendingSku = null;
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus({ preventScroll: true });
  }

  function closeTryOn() {
    if (!widget || !isOpen) return hideModal(); // still on the gender step: camera never started
    // Hide only after onStopVto, so the camera is properly released (FittingBox requirement).
    widget.stopVto();
    // Safety net in case onStopVto never arrives (e.g. camera permission never granted).
    clearTimeout(closeFallback);
    closeFallback = setTimeout(() => {
      isOpen = false;
      hideModal();
    }, 4000);
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal && modal.classList.contains('is-open')) closeTryOn();
  });

  function ensureWidget() {
    if (widgetPromise) return widgetPromise;

    widgetPromise = loadLibrary().then(
      () =>
        new Promise((resolve) => {
          buildModal();

          const params = {
            apiKey: cfg.apiKey,
            lang: cfg.lang,
            // Camera is released only once this fires (FittingBox checklist requirement).
            onStopVto: () => {
              isOpen = false;
              hideModal();
              log('stopped');
            },
            onIssue: (issue) => log('issue', issue),
          };

          widget = window.FitMix.createWidget(CONTAINER_ID, params, () => {
            log('ready');
            resolve(widget);
          });
        })
    );

    widgetPromise.catch((err) => {
      console.warn('[Kaleos VTO]', err);
      widgetPromise = null;
    });
    return widgetPromise;
  }

  /* ------------------------------------------------------------------ */
  /* Click → gender → camera                                             */
  /* ------------------------------------------------------------------ */

  function openTryOn(button) {
    if (isOpen || isStarting) return;
    lastTrigger = button;
    pendingSku = button.dataset.vtoSku;
    showGenderStep();
    ensureWidget().catch(() => {}); // usually already loaded in the background
  }

  async function startCamera(gender) {
    if (isStarting || !pendingSku) return;
    isStarting = true;
    modal.classList.add('is-loading');

    try {
      sessionStorage.setItem(GENDER_KEY, gender);
    } catch (e) {}

    try {
      const instance = await ensureWidget();
      if (!pendingSku) return; // closed while loading

      // Average PD for the chosen gender (f = 62 mm, m = 64 mm) → frame shown at the right size.
      instance.setUserInfo({ gender });
      instance.setFrame(pendingSku);
      log('start', { sku: pendingSku, gender });

      isOpen = true;
      modal.classList.remove('is-gender-step');
      instance.startVto('live');
      modal.querySelector('.vto-modal__close').focus({ preventScroll: true });
    } catch (err) {
      console.warn('[Kaleos VTO] could not open try-on', err);
      hideModal();
    } finally {
      isStarting = false;
      modal.classList.remove('is-loading');
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest(BUTTON_SELECTOR);
      if (!button || button.dataset.vtoState !== 'ready') return;
      // Buttons sit over product images/links: don't navigate or open zoom.
      event.preventDefault();
      event.stopPropagation();
      openTryOn(button);
    },
    true
  );

  /* ------------------------------------------------------------------ */
  /* Start + watch for new buttons                                       */
  /* ------------------------------------------------------------------ */

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (
          node.nodeType === 1 &&
          (node.matches(BUTTON_SELECTOR) || node.querySelector(BUTTON_SELECTOR))
        ) {
          queueScan();
          return;
        }
      }
    }
  });

  function start() {
    queueScan();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
